import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { InvoiceEnums } from 'src/app/enum/invoice-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { InvoiceDetailModel } from 'src/app/models/invoicing/invoice-detail.model';
import { InvoiceModel } from 'src/app/models/invoicing/invoice.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoiceDetailRegisterModel } from 'src/app/models/tables/invoicing/invoice-detail-register.model';
import { InvoiceDetailService } from 'src/app/services/invoicing/invoice-detail.service';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoice-detail',
  templateUrl: './invoice-detail.component.html',
  styleUrls: ['./invoice-detail.component.scss'],
  standalone: false
})
export class InvoiceDetailComponent implements OnChanges {
  @ViewChild('settlement') settlementSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() invoiceModel: InvoiceModel = new InvoiceModel();
  @Input() defaultStoreIdForBranch: number | null = null;
  @Input() companyId: number;
  @Input() disabled: boolean = false;
  @Input('isFormView')
  set changeFormView(change: boolean) {
    if (change) this.createNewDetail();
  }
  @Output() updateInvoice = new EventEmitter<InvoiceModel>();

  detailData: PagedResponseModel<InvoiceDetailRegisterModel> = new PagedResponseModel<InvoiceDetailRegisterModel>();
  detailHeader: ColumnResponseModel[] = [];
  invoiceDetail: InvoiceDetailModel = new InvoiceDetailModel();
  output$ = new Subject<any>();
  subscription: Subscription;
  isFormView: boolean = false;
  ascendant: boolean = true;
  columnSort: string | null = null;
  disabledButton: boolean = true;
  formInvoice: FormGroup = new FormGroup({});
  limit: number = 10;
  page: number = 1;
  tableActions: TableAction[] = [];
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  invoiceContract: any;

  constructor(private invoiceService: InvoiceService,
    private invoiceDetailService: InvoiceDetailService,
    private toastHandlerService: ToastHandlerService,) { }

  async ngOnInit(): Promise<void> {
    await this.getColumnsForDetailsInvoice();
    this.tableActions = this.getDefaultActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    const invoiceModel = changes["invoiceModel"];

    if (invoiceModel && invoiceModel.currentValue.invoiceId != 0) {
      this.getDetailsInvoiceWithPagination();
      this.invoiceDetail = new InvoiceDetailModel();
      this.disabledButton = this.invoiceModel.invoiceStateId != InvoiceEnums.abierta;
      this.tableActions = this.getDefaultActions();
    }
  }

  async createNewDetail() {
    this.invoiceDetail = new InvoiceDetailModel();
    this.getFormView(true);
  }

  async getColumnsForDetailsInvoice() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.invoiceDetailService.getColumnsForDetailsInvoice());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getDetailsInvoiceWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.invoiceDetailService.getInvoicesDetailsByInvoiceIdWithPagination(this.page,
        this.limit, this.columnSort, this.ascendant, this.invoiceModel.invoiceId));
    } catch (error) {
      console.error('Error al buscar los detalles de las facturas.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  clearRegister() {
    this.invoiceDetail = new InvoiceDetailModel();
  }

  async deleteInvoiceDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;
    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar el detalle la factura?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        await firstValueFrom(this.invoiceDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle de la factura fue eliminado exitosamente.');
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error al eliminar el detalle de factura.', error);
      }
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.invoiceModel.invoiceStateId == InvoiceEnums.abierta) {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    } else {
      return [];
    }
  }

  getInvoice(invoice: InvoiceModel) {
    this.updateInvoice.emit(invoice);
  }

  getInvoiceDetail(invoiceDetail: InvoiceDetailModel) {
    this.invoiceDetail = invoiceDetail;
  }

  async getUpdateListDetail() {
    this.getDetailsInvoiceWithPagination();
    this.getInvoice(await firstValueFrom(this.invoiceService.getInvoiceById(this.invoiceModel.invoiceId)));
  }

  getUpdateListDetailByDelete() {
    this.invoiceDetail = new InvoiceDetailModel();
    this.clearSelectionTrigger++;
    this.getUpdateListDetail();
  }

  async onAction(event: { action: string, item?: InvoiceDetailRegisterModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      const response = await firstValueFrom(this.invoiceDetailService.getInvoiceDetailById(event.item.invoiceDetailId));
      if (response) {
        this.invoiceDetail = response;
        this.isFormView = true;
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteInvoiceDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getDetailsInvoiceWithPagination();
  }


  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
